/**
 * useAppWatchdog — Runtime health monitoring hook.
 *
 * Responsibilities:
 *   1. Flush boot-phase errors from earlyErrorLogger
 *   2. Install auto-breadcrumbs (clicks, navigation, visibility, long tasks)
 *   3. Capture global errors → centralized errorStorage
 *   4. Blank screen detection after wake-up (Capacitor + web)
 *   5. IDB health check on foreground resume
 *
 * Mount ONCE in AppLayout (Tier 1 — fires for app lifetime).
 *
 * Inspired by v1's useAppWatchdog (424 lines) but redesigned to:
 *   - Use the centralized error stack (errorStorage + breadcrumbs + App Insights)
 *   - Integrate with AppLifecycleHub instead of raw listeners
 *   - Avoid console overrides (unreliable in production builds)
 *   - Use PerformanceObserver for long task detection (breadcrumbs.ts)
 */

import { useEffect, useRef } from "react"
import { Capacitor } from "@capacitor/core"
import { addBreadcrumb, installAutoBreadcrumbs } from "./breadcrumbs"
import { logError } from "./errorStorage"
import { flushEarlyErrors } from "./earlyErrorLogger"
import { handleIndexedDBError } from "@/shared/utils/platform/handleIndexedDBError"

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Delay (ms) after foreground resume before checking for blank screen */
const BLANK_SCREEN_CHECK_DELAY = 3000

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useAppWatchdog(): void {
  const mountedRef = useRef(false)

  useEffect(() => {
    if (mountedRef.current) return
    mountedRef.current = true

    // ── 1. Flush early boot errors ──
    void flushEarlyErrors()

    // ── 2. Install auto-breadcrumbs ──
    installAutoBreadcrumbs()

    addBreadcrumb({
      category: "lifecycle",
      message: "App watchdog mounted",
      data: {
        platform: Capacitor.getPlatform(),
        native: Capacitor.isNativePlatform(),
      },
    })

    // ── 3. Global error → errorStorage ──
    const handleError = (event: ErrorEvent) => {
      logError(event.error ?? new Error(event.message), {
        source: "global",
        severity: "error",
      })
      addBreadcrumb({
        category: "error",
        message: event.message,
        level: "error",
      })
    }

    const handleRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason
      logError(reason instanceof Error ? reason : new Error(String(reason)), {
        source: "global",
        severity: "error",
      })
      addBreadcrumb({
        category: "error",
        message: reason instanceof Error ? reason.message : String(reason),
        level: "error",
      })
    }

    window.addEventListener("error", handleError)
    window.addEventListener("unhandledrejection", handleRejection)

    // ── 4. Blank screen detection after wake-up ──
    let blankScreenTimer: ReturnType<typeof setTimeout> | null = null

    const checkBlankScreen = () => {
      const root = document.getElementById("root")
      if (!root) return

      // Check if root has visible content
      const hasContent =
        root.children.length > 0 &&
        root.offsetHeight > 0 &&
        root.innerHTML.length > 100 // Minimal React output

      if (!hasContent) {
        logError(new Error("Blank screen detected after wake-up"), {
          source: "watchdog",
          category: "render",
          severity: "critical",
          data: {
            rootChildren: root.children.length,
            rootHeight: root.offsetHeight,
            innerHTMLLength: root.innerHTML.length,
          },
        })

        addBreadcrumb({
          category: "lifecycle",
          message: "⚠️ Blank screen detected — reloading",
          level: "error",
        })

        // Attempt recovery via reload
        window.location.reload()
      }
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        // Schedule blank screen check after a delay
        // (give React time to re-render after wake)
        blankScreenTimer = setTimeout(checkBlankScreen, BLANK_SCREEN_CHECK_DELAY)

        // IDB health check on foreground (catches WebKit deadlock after backgrounding)
        checkIDBHealth()
      } else {
        // Cancel if app goes back to background
        if (blankScreenTimer) {
          clearTimeout(blankScreenTimer)
          blankScreenTimer = null
        }
      }
    }

    document.addEventListener("visibilitychange", handleVisibilityChange)

    // ── 5. IDB health check on native resume ──
    let nativeCleanup: (() => void) | null = null

    if (Capacitor.isNativePlatform()) {
      void setupNativeResumeCheck().then((cleanup) => {
        nativeCleanup = cleanup
      })
    }

    // ── Cleanup ──
    return () => {
      window.removeEventListener("error", handleError)
      window.removeEventListener("unhandledrejection", handleRejection)
      document.removeEventListener("visibilitychange", handleVisibilityChange)
      if (blankScreenTimer) clearTimeout(blankScreenTimer)
      nativeCleanup?.()
    }
  }, [])
}

// ---------------------------------------------------------------------------
// Native resume — IDB health check
// ---------------------------------------------------------------------------

async function setupNativeResumeCheck(): Promise<() => void> {
  try {
    const { App } = await import("@capacitor/app")

    const handle = await App.addListener("appStateChange", (state) => {
      if (state.isActive) {
        addBreadcrumb({
          category: "lifecycle",
          message: "Native app resumed (foreground)",
        })

        // Quick IDB health check: try to open the database
        checkIDBHealth()
      } else {
        addBreadcrumb({
          category: "lifecycle",
          message: "Native app backgrounded",
        })
      }
    })

    return () => {
      void handle.remove()
    }
  } catch {
    return () => {}
  }
}

/**
 * Real IndexedDB health check — probes the ACTUAL app database, not a test DB.
 *
 * Two recovery paths:
 * 1. CRASH (IDB throws immediately): error matches known patterns → reload INSTANTLY
 * 2. DEADLOCK (IDB hangs forever): 10s timeout → reload
 *
 * When WebKit's IDB connection is corrupted after backgrounding, either:
 * - Transactions throw with "in-progress transaction" errors → instant reload
 * - navigator.locks callback never fires → nothing happens → 10s timeout
 */
function checkIDBHealth(): void {
  const HEALTH_TIMEOUT_MS = 10_000

  let resolved = false
  const timer = setTimeout(() => {
    if (resolved) return
    resolved = true

    addBreadcrumb({
      category: "error",
      message: "⚠️ IDB health check timeout — DB deadlocked, reloading",
      level: "error",
    })

    logError(new Error("IDB health check: database read timed out after 10s (deadlock)"), {
      source: "watchdog",
      category: "indexeddb",
      severity: "critical",
    })

    // IDB is deadlocked — reload to recover
    if (document.visibilityState === "visible") {
      window.location.reload()
    }
  }, HEALTH_TIMEOUT_MS)

  // Try a trivial read on the real database
  import("@/db/schema").then(({ db }) => {
    // Use .count() on setting_local (tiny table, private, always available)
    db.table("setting_local").count().then(() => {
      if (resolved) return
      resolved = true
      clearTimeout(timer)
      // IDB is healthy — no action needed
    }).catch((err: unknown) => {
      if (resolved) return
      resolved = true
      clearTimeout(timer)

      // IDB threw an error — check if it matches known crash patterns.
      // handleIndexedDBError will reload IMMEDIATELY if it matches.
      // This is the INSTANT path (no 10s wait).
      handleIndexedDBError(err)
    })
  }).catch(() => {
    // Module import failed — can't check, clear timer
    if (!resolved) {
      resolved = true
      clearTimeout(timer)
    }
  })
}
